import React from 'react';
import moment from 'moment-timezone';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import CoachingConstants from 'coaching/CoachingConstants';
import { reserveCoachingSession } from 'coaching/actions/CoachingActions';
import CoachingHeading from 'coaching/components/CoachingHeading';
import {
  CoachingReservation,
  SavedCoachingReservation,
  CoachingTimeOff,
} from 'coaching/containers/GetAvailableReservationsContainer';
import CoachingSelfServeTimeOffView from 'coaching/components/CoachingSelfServeView/CoachingSelfServeTimeOffView';
import Copy from 'components/Copy';
import DateTimeAvailabilitySelect from 'components/DateTimeAvailabilitySelect';
import ErrorReporter from 'app/components/ErrorReporter';
import EventTracking from 'lib/EventTracking';
import Image from 'components/Image';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Select from 'components/Select';
import Text from 'components/Text';
import { WaitlistCopyAndLink } from 'coaching/components/TutoringSelfServeView/TutoringSelfServeViewSchedule/TutoringScheduleAvailabilitySelection';
import TimezoneHelper from 'lib/timezones';
// @ts-ignore
import userAgent from 'lib/currentUserAgent';
import ToastActions from 'actions/ToastActions';
import CurrentUser from 'auth/CurrentUser';

import StarsDoodleIllustration from 'assets/images/coaching/stars-animated-css.svg';

type Props = {
  selectedSubject: string;
  disabledContinueButton: boolean;
  daysToShow: moment.Moment[];
  userTimezone: string;
  selectedReservation?: CoachingReservation;
  reservations?: CoachingReservation[];
  timeOff?: CoachingTimeOff[];
  loading: boolean;
  error?: string;
  onSelectReservation: (reservation: CoachingReservation) => void;
  onSelectTimezone: (tz: string) => void;
  onContinue: () => void;
};

type State = {
  submittingReservation: boolean;
  weeklySessionDayOfWeek: string;
  weeklySessionTime: string;
};

export default class CoachingSelfServeScheduleSessionView extends React.Component<Props, State> {
  state = {
    submittingReservation: false,
    weeklySessionDayOfWeek: '',
    weeklySessionTime: '',
  };

  componentDidMount() {
    this.trackEvent('view');

    const { selectedReservation } = this.props;
    if (selectedReservation) {
      this.setWeeklyDayAndTime(selectedReservation);
    }
  }

  eventPrefix = 'coaching.self_schedule_purchase.schedule_session';

  trackEvent(key: string) {
    EventTracking.trackKey(`${this.eventPrefix}.${key}`);
  }

  setWeeklyDayAndTime(reservation: CoachingReservation) {
    const { startDateTime } = reservation;
    this.setState({
      weeklySessionDayOfWeek: startDateTime.format('dddd'),
      weeklySessionTime: startDateTime.format('LT'),
    });
  }

  handleSelectDate = (date: moment.Moment) => {
    this.trackEvent('time.click');

    const errorToastText =
      'Oops! Looks like something went wrong - please refresh the page and try again.';
    const { reservations } = this.props;
    if (!reservations) {
      ToastActions.showErrorToast(errorToastText);
      console.error(
        'CoachingSelfServeScheduleSessionView - tried to select a date without any reservations loaded'
      );
      return;
    }

    const selectedReservation = reservations.find(res =>
      moment.utc(res.startDateTime).isSame(date, 'second')
    );
    if (!selectedReservation) {
      ToastActions.showErrorToast(errorToastText);
      console.error(
        'CoachingSelfServeScheduleSessionView - tried to select a date without a corresponding reservation'
      );
      return;
    }

    this.setWeeklyDayAndTime(selectedReservation);

    this.props.onSelectReservation(selectedReservation);
  };

  handleContinue = async () => {
    const { onContinue, onSelectReservation, selectedReservation, selectedSubject } = this.props;
    this.setState({ submittingReservation: true });
    const reservation = selectedReservation as CoachingReservation;
    const reservationMetadata = await reserveCoachingSession(
      reservation.startDateTime.toISOString(),
      reservation.endDateTime.toISOString(),
      reservation.coachUuid,
      CurrentUser.getUser().getUuid(),
      selectedSubject
    );
    this.setState({ submittingReservation: false });
    // If it failed, don't advance to the purchase screen
    if (reservationMetadata === null) return;
    onSelectReservation({ ...reservation, ...reservationMetadata } as SavedCoachingReservation);
    onContinue();
  };

  renderAvailabilities = () => {
    const {
      daysToShow,
      selectedReservation,
      loading,
      userTimezone,
      reservations,
      onSelectTimezone,
    } = this.props;
    if (loading) {
      return (
        <Row>
          <BoxBody>
            <Text align="center">
              <Row size="xsmall">
                <Image src={StarsDoodleIllustration} />
              </Row>
              <Text weight="semibold" color="graphite">
                Checking availability
              </Text>
            </Text>
          </BoxBody>
        </Row>
      );
    }

    const dateTimeOptionsInUserTimezone =
      reservations?.map(r => r.startDateTime.tz(userTimezone)) ?? [];
    return (
      <>
        <Row size="xsmall">
          <MediaObject center gutter="xsmall">
            <Text color="graphite">Times shown in</Text>
            <Select
              inline
              value={userTimezone}
              onChange={(tz: string) => {
                this.trackEvent('timezone.click');
                onSelectTimezone(tz);
              }}
              options={TimezoneHelper.USTimezoneSelectOptions.filter(
                option =>
                  !(CoachingConstants.TIMEZONES_TO_EXCLUDE as ReadonlyArray<string>).includes(
                    option.label
                  )
              )}
            />
          </MediaObject>
        </Row>
        <Row size="small">
          {/*
            <DateTimeAvailabilitySelect
              data-test=CoachingSessionsBySubjectBox.tsx"reservation-date-time-select"
              box
              daysPerRow={6}
              slotsPerRow={userAgent.isDesktop() ? 4 : 3}
              value={selectedReservation?.startDateTime}
              onChange={this.handleSelectDate}
              daysToShow={daysToShow}
              dateTimeOptions={dateTimeOptionsInUserTimezone}
              onSelectDay={() => this.trackEvent('date.click')}
              onMoreAvailabilities={() => this.trackEvent('show_more.click')}
              onLessAvailabilities={() => this.trackEvent('show_less.click')}
              onEmptyDay={(day: string) => {
                if (!loading) {
                  console.error(
                    `CoachingSelfServeScheduleSessionView - could not find any open slots on ${day} for ${this.props.selectedSubject}`
                  );
                }
              }}
            />
            */}
        </Row>
      </>
    );
  };

  render() {
    const { disabledContinueButton, error, timeOff } = this.props;
    const { weeklySessionDayOfWeek, weeklySessionTime } = this.state;
    if (error) {
      return (
        <ErrorReporter
          error={`CoachingSelfServeScheduleSessionView - could not load reservations e=${error}`}>
          Oops, looks like something went wrong - please try refreshing the page.
        </ErrorReporter>
      );
    }

    return (
      <React.Fragment>
        <CoachingHeading>Schedule your first session</CoachingHeading>
        <Row>
          <Copy align="left" size="small">
            This full-length session will be 40 minutes. Tutoring is available Monday through
            Sunday.
          </Copy>
        </Row>
        {this.renderAvailabilities()}
        {timeOff && <CoachingSelfServeTimeOffView timeOff={timeOff} />}
        <WaitlistCopyAndLink
          flowVersion="old"
          selectedSlotStart={this.props.selectedReservation?.startDateTime ?? null}
          selectedSlotEnd={this.props.selectedReservation?.endDateTime ?? null}
        />
        <Row size="small">
          <Button
            data-test="schedule-session-continue-button"
            color="koolaid"
            eventKey={`${this.eventPrefix}.continue.click`}
            onClick={this.handleContinue}
            disabled={disabledContinueButton || this.state.submittingReservation}>
            Select
          </Button>
        </Row>
        {weeklySessionDayOfWeek && weeklySessionTime && (
          <Text size="xsmall" color="graphite">
            We’ll reserve{' '}
            <Text weight="semibold">
              every {weeklySessionDayOfWeek} at {weeklySessionTime}
            </Text>{' '}
            for you with your coach. Don’t worry, you can always adjust your schedule for future
            sessions.
          </Text>
        )}
      </React.Fragment>
    );
  }
}
